import { Turnstile } from "@marsidev/react-turnstile";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  getPublicVariantConfig,
  type PublicVariantConfig,
} from "../pages/jan-public/api/publicChat";
import { EmailGateModal } from "../pages/jan-public/components/EmailGateModal";
import { PublicChatShell } from "../pages/jan-public/components/PublicChatShell";
import { usePublicChatMachine } from "../pages/jan-public/hooks/usePublicChatMachine";
import { useTurnstileToken } from "../pages/jan-public/hooks/useTurnstileToken";
import { getChatId, getToken } from "../pages/jan-public/utils/sessionToken";
import {
  TURNSTILE_ACTION,
  TURNSTILE_SITE_KEY,
} from "../pages/jan-public/utils/turnstileConfig";

/**
 * Variant-aware React root for the embeddable Jan widget. Differs from
 * PublicJanPage in three ways the embed requires:
 *   1. LAZY bootstrap — only the session-less config is fetched on mount;
 *      the actual session is minted on the FIRST user submit.
 *   2. Turnstile is PORTALLED to the light DOM (document.body) because the
 *      Cloudflare script crashes inside a shadow root (Task 1 spike).
 *   3. Message action buttons (Copy/PDF/DOCX) are hidden.
 * Returning visitors (existing token for this variant) skip Turnstile.
 */
export function EmbeddedJanApp({ variant }: { variant: string }) {
  const [config, setConfig] = useState<PublicVariantConfig | null>(null);
  const [configError, setConfigError] = useState(false);
  const [turnstileActive, setTurnstileActive] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  // Holds the in-flight (or settled) bootstrap so a second submit during
  // bootstrap awaits the SAME promise instead of racing ahead to submit with no
  // chat id. Reset to null on failure so the next submit retries.
  const bootstrapInFlightRef = useRef<Promise<void> | null>(null);

  // Turnstile is mounted on demand (first bootstrap or 401 recovery); the widget
  // lives in a light-DOM portal because Cloudflare's script crashes in shadow DOM.
  const { widgetRef, requestToken, deliverToken } = useTurnstileToken({
    enabled: Boolean(TURNSTILE_SITE_KEY),
    onMountNeeded: () => setTurnstileActive(true),
  });

  useEffect(() => {
    let alive = true;
    getPublicVariantConfig(variant)
      .then((c) => {
        if (alive) setConfig(c);
      })
      .catch(() => {
        if (alive) setConfigError(true);
      });
    return () => {
      alive = false;
    };
  }, [variant]);

  const chat = usePublicChatMachine({
    variant,
    getTurnstileToken: requestToken,
  });

  const ensureBootstrapped = useCallback((): Promise<void> => {
    // Already running or done — return the same promise so a concurrent submit
    // waits for it rather than starting a second bootstrap or racing to submit.
    if (bootstrapInFlightRef.current) return bootstrapInFlightRef.current;

    const promise = (async () => {
      try {
        // Only an existing token AND chat id let us reuse a session; with just
        // one we still need to mint (and therefore a Turnstile token).
        const hasSession = Boolean(getToken(variant) && getChatId(variant));
        let turnstileToken: string | undefined;
        if (!hasSession && TURNSTILE_SITE_KEY) {
          // requestToken mounts the widget when none is mounted yet.
          turnstileToken = await requestToken();
        }
        // bootstrap() swallows its own errors (surfaced via state) and resolves
        // either way, so a null chat id is how we detect that it failed.
        const chatId = await chat.bootstrap({ turnstileToken });
        if (!chatId) throw new Error("bootstrap_failed");
      } catch (e) {
        bootstrapInFlightRef.current = null; // let the next submit retry
        throw e;
      }
    })();
    bootstrapInFlightRef.current = promise;
    return promise;
  }, [variant, requestToken, chat]);

  const handleSubmit = useCallback(
    async (message: string) => {
      try {
        await ensureBootstrapped();
      } catch {
        // Bootstrap failed (Turnstile error/timeout, session-start rejected).
        // Don't submit against a session we don't have; the user can retry.
        return;
      }
      await chat.submit(message);
    },
    [ensureBootstrapped, chat]
  );

  const onTurnstileSuccess = (token: string): void => {
    deliverToken(token);
    // Unmount the widget so the Cloudflare box doesn't linger on screen; a later
    // 401 recovery re-mounts it on demand (requestToken -> onMountNeeded).
    setTurnstileActive(false);
  };

  // Deliver "" (sentinel): the backend rejects an empty token cleanly, surfacing
  // the failure via the normal error path; rejecting here would need a separate
  // catch branch.
  const onTurnstileError = (): void => {
    deliverToken("");
    setTurnstileActive(false);
  };

  const onSubmitEmail = async (
    email: string,
    professionId: string
  ): Promise<void> => {
    setEmailSubmitting(true);
    try {
      await chat.submitEmail(email, professionId);
    } finally {
      setEmailSubmitting(false);
    }
    await chat.retryPending();
  };

  if (configError) {
    return (
      <div className="p-4 text-sm text-slate-600">
        This chat isn&apos;t available right now.
      </div>
    );
  }

  // Hold the shell until config loads, else the generic default prompts flash
  // before the variant's welcome/starters arrive.
  if (!config) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <div
          className="w-8 h-8 border-2 border-slate-300 rounded-full animate-spin"
          style={{ borderTopColor: "#05BDFF" }}
        />
      </div>
    );
  }

  return (
    <>
      <PublicChatShell
        status={chat.status}
        messages={chat.messages}
        streamingContent={chat.streamingContent}
        error={chat.error}
        onSubmit={handleSubmit}
        welcome={config.welcome}
        starters={config.starters}
        showMessageActions={false}
        embedded
      />

      {turnstileActive && TURNSTILE_SITE_KEY
        ? createPortal(
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2147483647,
              }}
            >
              <Turnstile
                ref={widgetRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ action: TURNSTILE_ACTION }}
                onSuccess={onTurnstileSuccess}
                onExpire={() => widgetRef.current?.reset()}
                onError={onTurnstileError}
              />
            </div>,
            document.body
          )
        : null}

      {chat.status === "awaiting-email" ? (
        <EmailGateModal onSubmit={onSubmitEmail} submitting={emailSubmitting} />
      ) : null}
    </>
  );
}
